// src/hooks/useBlog.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { blogService } from '../services/blogService';
import blogSearchIndex from '../services/blogSearchService';
import { useToast } from './useToast';

export const useBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [isSearchReady, setIsSearchReady] = useState(false);
  const [seoScore, setSeoScore] = useState(null);
  const { showToast } = useToast();
  const abortControllerRef = useRef(null);

  // Initialize search index
  useEffect(() => {
    const initSearch = async () => {
      await blogSearchIndex.loadIndex();
      setIsSearchReady(true);
    };
    initSearch();
  }, []);

  const fetchBlogs = useCallback(
    async (options = {}) => {
      // If search is active, use instant search
      if (options.search && options.search.trim() !== '' && isSearchReady) {
        setLoading(false);

        const result = blogSearchIndex.search(options.search, {
          category: options.category,
          tag: options.tag,
          sortBy: options.sortBy || 'publishedAt',
          sortOrder: options.sortOrder || 'desc',
          limit: options.limit || 9,
          page: options.page || 1,
        });

        setBlogs(result.blogs);
        setPagination({
          currentPage: result.currentPage,
          totalPages: result.totalPages,
          totalItems: result.totalItems,
          hasNext: result.hasNext,
          hasPrev: result.hasPrev,
        });
        return result;
      }

      // Regular API fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoading(true);
      setError(null);

      try {
        const response = await blogService.getBlogs(options);
        const blogsData = response.blogs || [];
        const paginationData = response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          hasNext: false,
          hasPrev: false,
        };

        setBlogs(blogsData);
        setPagination(paginationData);
        return response;
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Request cancelled');
          return;
        }
        const errorMessage = err.message || 'Failed to load blogs';
        setError(errorMessage);
        console.error('fetchBlogs error:', err);
        if (options.showToast !== false) {
          showToast(errorMessage, 'error');
        }
        throw err;
      } finally {
        setLoading(false);
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [showToast, isSearchReady],
  );

  const fetchBlogBySlug = useCallback(
    async slug => {
      // Try instant search first
      if (isSearchReady) {
        const blog = blogSearchIndex.getBlogBySlug(slug);
        if (blog) {
          return blog;
        }
      }

      setLoading(true);
      setError(null);
      try {
        const response = await blogService.getBlogBySlug(slug);
        return response.blog;
      } catch (err) {
        const errorMessage = err.message || 'Failed to load blog post';
        setError(errorMessage);
        showToast(errorMessage, 'error');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showToast, isSearchReady],
  );

  const fetchCategories = useCallback(async () => {
    if (isSearchReady) {
      return blogSearchIndex.getCategories();
    }

    try {
      const response = await blogService.getCategories();
      return response.categories || [];
    } catch (err) {
      console.error('fetchCategories error:', err);
      return [];
    }
  }, [isSearchReady]);

  const fetchTags = useCallback(async () => {
    if (isSearchReady) {
      return blogSearchIndex.getTags();
    }

    try {
      const response = await blogService.getTags();
      return response.tags || [];
    } catch (err) {
      console.error('fetchTags error:', err);
      return [];
    }
  }, [isSearchReady]);

  const fetchRelatedPosts = useCallback(async (id, category, tags, slug) => {
    try {
      const response = await blogService.getRelatedPosts(
        id,
        category,
        tags,
        slug,
      );
      return response.relatedPosts || [];
    } catch (err) {
      console.error('fetchRelatedPosts error:', err);
      return [];
    }
  }, []);

  const fetchSEOScore = useCallback(async blogId => {
    try {
      const response = await blogService.getSEOScore(blogId);
      setSeoScore(response.score);
      return response.score;
    } catch (err) {
      console.error('fetchSEOScore error:', err);
      return null;
    }
  }, []);

  const refreshSearchIndex = useCallback(async () => {
    blogSearchIndex.clear();
    await blogSearchIndex.loadIndex();
    setIsSearchReady(true);
  }, []);

  return {
    blogs,
    loading,
    error,
    pagination,
    isSearchReady,
    seoScore,
    fetchBlogs,
    fetchBlogBySlug,
    fetchCategories,
    fetchTags,
    fetchRelatedPosts,
    fetchSEOScore,
    refreshSearchIndex,
  };
};
